/* eslint-disable no-param-reassign, consistent-return */
import React, {
  useRef,
  useState,
  useEffect,
} from 'react';
import i18n from 'i18next';

import {
  useClickAway,
} from 'web-hooks';
import {
  placeCaretAtEnd,
} from 'web-utils';

import {
  Icon,
  Select,
  Dropdown,
  AssignSearchOption,
} from 'web-components';

import {
  Text,
} from 'web-styled';

import * as S from './styled';

type Props = {
  week: any,
  workoutOptions: any[],
  dragHandleProps: any,
  onCopy: { (): void },
  onUpdate: { (values: any): void },
  onRemove: { (): void },
  onAddWorkout: { (): void },
};

const WeekHeader = ({
  week,
  workoutOptions,
  dragHandleProps,
  onCopy,
  onUpdate,
  onRemove,
  onAddWorkout,
}: Props) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState<string>(week.name || '');
  const [nameIsEditable, setNameEditable] = useState<boolean>(false);
  const [searchVisible, setSearchVisible] = useState<boolean>(false);

  useEffect(() => {
    setName(week.name || '');
  }, [week.id]);

  const ref = useClickAway(
    () => {
      setSearchVisible(false);
    },
    { disabled: !searchVisible },
  );

  const startEdit = () => {
    setNameEditable(true);
    setTimeout(() => {
      if (nameRef.current) {
        placeCaretAtEnd(nameRef.current);
        nameRef.current.scrollLeft = nameRef.current.scrollWidth;
      }
    }, 0);
  };

  const actions = [
    {
      name: i18n.t('add.workout'),
      onClick: () => setSearchVisible(true),
    },
    {
      name: 'Rename',
      onClick: () => startEdit(),
    },
    {
      name: 'Duplicate',
      onClick: () => onCopy(),
    },
    {
      name: 'Delete',
      color: '#e54d42',
      onClick: () => onRemove(),
    },
  ];

  return (
    <S.Container ref={ref}>
      <S.Sort
        {...dragHandleProps}
      >
        <Icon type="fas fa-arrows-alt-v" />
      </S.Sort>

      {nameIsEditable
        ? (
          <S.EditableWeekName
            id={`id-${week.id}`}
            html={name}
            innerRef={nameRef}
            onBlur={(e) => {
              const newName = e.target.textContent || '';
              if (newName && newName !== week.name) {
                setName(newName);
                onUpdate({
                  name: newName,
                });
              }
              setNameEditable(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.target.blur();
              }
            }}
          />
        ) : (
          <S.WeekName
            dangerouslySetInnerHTML={{ __html: name }}
          />
        )}

      {searchVisible && (
        <S.Search>
          <Select
            value=""
            autoFocus
            menuIsOpen
            creatable
            placeholder={i18n.t('search.placeholder.workoutTemplates')}
            createOptionPosition="first"
            components={{ Option: AssignSearchOption }}
            menuPlacement="auto"
            formatCreateLabel={userInput => (
              <Text color="primary">
                Add
                {' '}
                <b>{userInput}</b>
              </Text>
            )}
            options={[
              {
                newOne: true,
                value: 'new',
                label: 'CREATE NEW',
              },
              ...workoutOptions,
            ]}
            styles={{
              menu: provided => ({
                ...provided,
                marginTop: '1px',
              }),
              menuList: provided => ({
                ...provided,
                maxHeight: '220px',
              }),
            }}
            onChange={(option) => {
              setSearchVisible(false);
              if (option?.value) {
                onAddWorkout(option);
              }
            }}
          />
        </S.Search>
      )}

      <Dropdown
        trigger={(
          <S.ActionsTrigger>
            <S.ContextMenu />
          </S.ActionsTrigger>
        )}
        items={actions}
      />
    </S.Container>
  );
};

export default WeekHeader;
